/**
 * @ipc/validation · src/schemas/links.ts — форма создания платёжной ссылки в admin-ui
 * (раздел 05 × 14, `POST /api/v1/links`, admin-ui-ветка контракта: `merchantId` +
 * `amountTiyin`, JWT без merchant-scope). Дословно повторяет форму тела
 * `CreateInvoiceInputSchema` (apps/backend/src/domains/invoices/schemas.ts) для
 * полей, которые реально заполняет форма admin-ui — не весь суперсет (без
 * `amountSum`/`billingLines`/`ofd`/`idempotencyKey`: сумма конвертируется в
 * тийины на границе UI, идемпотентность — заголовок `Idempotency-Key`).
 *
 * Русские сообщения — для мгновенного feedback react-hook-form + zodResolver.
 *
 * Также: server-side list filters (`paymentLinkFiltersSchema`) и bulk-cancel
 * (`paymentLinkBulkSchema`) для GET/POST `/api/v1/links` (раздел 05/13).
 */
import { z } from 'zod/v4';
import { phoneSchema, langSchema, paginationSchema, searchSchema, sortOrderSchema } from '../common/primitives.ts';
import { paymentMethodSchema } from '../enums/payment-methods.ts';

export const createPaymentLinkSchema = z
  .object({
    merchantId: z
      .number('Укажите ID мерчанта')
      .int('ID мерчанта должен быть целым числом')
      .positive('ID мерчанта должен быть положительным'),
    amountSum: z
      .number('Укажите сумму')
      .positive('Сумма должна быть больше нуля'),
    invoiceId: z
      .string()
      .min(1, 'Invoice ID обязателен')
      .max(255, 'Invoice ID: не более 255 символов'),
    description: z.string().max(1000, 'Описание: не более 1000 символов').optional(),
    lang: langSchema,
    ttlSeconds: z.number().int().positive('TTL должен быть положительным целым').optional(),
    sms: phoneSchema.optional().or(z.literal('')),
    returnUrl: z.string().url('Некорректный URL').optional().or(z.literal('')),
    returnErrorUrl: z.string().url('Некорректный URL').optional().or(z.literal('')),
    /**
     * ИКПУ (mxik) и код упаковки для ofd-позиции фискального чека.
     * Оба пустые — ofd не отправляется (Multicard возьмёт tax_* кассы).
     * Заполнены оба — одна ofd-позиция на всю сумму ссылки.
     */
    mxik: z.string().max(32).optional().or(z.literal('')),
    packageCode: z.string().max(32).optional().or(z.literal('')),
    /** ID действий (PaymentAction) после успешной оплаты. */
    actionIds: z.array(z.number().int().positive()).max(20).optional(),
    /**
     * Что показать плательщику. Один способ — ссылка сразу ведёт в него,
     * несколько — страница выбора `/pay/l/:publicId`.
     */
    paymentMethods: z.array(paymentMethodSchema).min(1, 'Выберите хотя бы один способ оплаты').optional(),
  })
  .superRefine((v, ctx) => {
    const mxik = (v.mxik ?? '').trim();
    const packageCode = (v.packageCode ?? '').trim();
    if (mxik.length === 0 && packageCode.length === 0) {
      return;
    }
    if (mxik.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['mxik'],
        message: 'Укажите ИКПУ вместе с кодом упаковки (или очистите оба)',
      });
    }
    if (packageCode.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['packageCode'],
        message: 'Укажите код упаковки вместе с ИКПУ (или очистите оба)',
      });
    }
  });
export type CreatePaymentLinkFormDto = z.infer<typeof createPaymentLinkSchema>;

/** Канонический PaymentLinkStatus (раздел 02) — используется фильтром списка/бейджем. */
export const PAYMENT_LINK_STATUSES = ['ACTIVE', 'PAID', 'EXPIRED', 'CANCELLED'] as const;
export const paymentLinkStatusSchema = z.enum(PAYMENT_LINK_STATUSES);
export type PaymentLinkStatus = z.infer<typeof paymentLinkStatusSchema>;

/** Колонки серверной сортировки списка ссылок (GET /api/v1/links). */
export const PAYMENT_LINK_SORT_COLUMNS = ['createdAt', 'amountTiyin', 'status'] as const;
export const paymentLinkSortBySchema = z.enum(PAYMENT_LINK_SORT_COLUMNS);
export type PaymentLinkSortBy = z.infer<typeof paymentLinkSortBySchema>;

/**
 * GET /api/v1/links — page-based пагинация + фильтры + сортировка
 * (эталон payments, единый meta.pagination).
 */
export const paymentLinkFiltersSchema = paginationSchema.extend({
  status: paymentLinkStatusSchema.optional(),
  invoiceId: z.string().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  search: searchSchema,
  sortBy: paymentLinkSortBySchema.default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type PaymentLinkFiltersDto = z.infer<typeof paymentLinkFiltersSchema>;

/**
 * POST /api/v1/links/bulk — массовая отмена. `ids` — providerUuid (публичный
 * идентификатор ссылки в API; внутренний bigint id наружу не отдаётся).
 * Отменяются только ACTIVE (PAID/EXPIRED/CANCELLED — в failed с причиной).
 */
export const paymentLinkBulkSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(100),
  action: z.literal('cancel'),
});
export type PaymentLinkBulkDto = z.infer<typeof paymentLinkBulkSchema>;
